import { tokenManager } from "../utils/tokenManager";
import { userTimezoneHeaders } from "../utils/userTimezoneHeader";

export class ApiRequestError extends Error {
  code: string;

  /** HTTP status when the failure came from `fetch` (0 if unknown). */
  status: number;

  constructor(message: string, code = "REQUEST_FAILED", status = 0) {
    super(message);
    this.name = "ApiRequestError";
    this.code = code;
    this.status = status;
  }
}

export function getOmniWatchBaseUrl(): string {
  const raw =
    process.env.REACT_APP_WAHANKHEDE_360_API_URL ||
    process.env.REACT_APP_OMNI_WATCH_API_URL ||
    "";
  const s = String(raw).trim().replace(/\/$/, "");
  return s;
}

/** Adonis proxy: `/api/streams/hls/...` (not legacy `/api/v1/streams/...`). */
export function normalizeStreamMediaPath(path: string): string {
  return String(path).replace(/\/api\/v1\/streams\//gi, "/api/streams/");
}

function isStreamOrRecordingPath(path: string): boolean {
  const p = normalizeStreamMediaPath(path);
  return (
    p.startsWith("/api/streams") || p.startsWith("/api/recordings")
  );
}

function devStreamBaseForPageOrigin(): string {
  const node =
    process.env.REACT_APP_NODE_API_URL?.trim().replace(/\/$/, "") || "";
  const omni = getOmniWatchBaseUrl();
  return node || omni;
}

/**
 * In dev, HLS should load via the React dev server origin so `package.json`
 * `"proxy": "http://localhost:5000"` forwards `/api/streams/...` to Node.
 */
function shouldResolveStreamsViaPageOrigin(relativePath: string): boolean {
  if (typeof window === "undefined" || !window.location?.origin) return false;
  if (!isStreamOrRecordingPath(relativePath)) return false;
  const configuredBase = devStreamBaseForPageOrigin();
  if (!configuredBase) return true;
  try {
    const apiOrigin = new URL(configuredBase).origin;
    const pageOrigin = window.location.origin;
    if (apiOrigin === pageOrigin) return false;
    const isLocal = (o: string) => {
      const h = new URL(o).hostname;
      return h === "localhost" || h === "127.0.0.1";
    };
    return isLocal(apiOrigin) && isLocal(pageOrigin);
  } catch {
    return false;
  }
}

/**
 * Resolve stream/snapshot paths for HLS.js (playlists + segments).
 */
export function resolveOmniMediaUrl(
  pathOrAbsolute: string | null | undefined,
): string {
  if (pathOrAbsolute == null) return "";
  const s = String(pathOrAbsolute).trim();
  if (!s) return "";

  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      u.pathname = normalizeStreamMediaPath(u.pathname);
      if (
        isStreamOrRecordingPath(u.pathname) &&
        shouldResolveStreamsViaPageOrigin(u.pathname)
      ) {
        return `${window.location.origin}${u.pathname}${u.search}`;
      }
      return u.toString();
    } catch {
      return s;
    }
  }

  const p = normalizeStreamMediaPath(s.startsWith("/") ? s : `/${s}`);
  if (shouldResolveStreamsViaPageOrigin(p)) {
    return `${window.location.origin}${p}`;
  }

  const base = getOmniWatchBaseUrl();
  if (base) {
    return `${base}${p}`;
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${p}`;
  }
  return p;
}

/**
 * Request URL for Omni Watch JSON APIs.
 * - If `REACT_APP_WAHANKHEDE_360_API_URL` / `REACT_APP_OMNI_WATCH_API_URL` is set:
 *   `${origin}/api/v1/...` on that host.
 * - If unset: path only (e.g. `/api/v1/cameras`) → same origin as the React app,
 *   so Create React App `package.json` `"proxy"` forwards to your backend in dev.
 */
function buildUrl(path: string): string {
  const base = getOmniWatchBaseUrl();
  const p = path.startsWith("/") ? path : `/${path}`;
  if (!base) return p;
  return `${base}${p}`;
}

function parseJsonError(data: unknown): { message: string; code: string } {
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    const message =
      (typeof o.message === "string" && o.message) ||
      (typeof o.detail === "string" && o.detail) ||
      (typeof o.error === "string" && o.error) ||
      "Request failed";
    const code =
      (typeof o.code === "string" && o.code) ||
      (typeof o.error === "string" && o.error) ||
      "REQUEST_FAILED";
    return { message, code };
  }
  return { message: "Request failed", code: "REQUEST_FAILED" };
}

export async function apiGet<T>(path: string): Promise<T> {
  const url = buildUrl(path);
  const token = tokenManager.getToken();
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...userTimezoneHeaders(),
    },
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const { message, code } = parseJsonError(data);
    if (
      data &&
      typeof data === "object" &&
      (data as { loggedIn?: boolean }).loggedIn === false
    ) {
      tokenManager.clearAuth();
      window.location.href = "/login";
    }
    throw new ApiRequestError(message, code, res.status);
  }
  return data as T;
}

/**
 * GET JSON from a fully qualified URL (e.g. Node API on another origin).
 * Uses the same auth and error handling as {@link apiGet}.
 */
export async function apiGetAbsoluteUrl<T>(fullUrl: string): Promise<T> {
  const token = tokenManager.getToken();
  const res = await fetch(fullUrl, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...userTimezoneHeaders(),
    },
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const { message, code } = parseJsonError(data);
    if (
      data &&
      typeof data === "object" &&
      (data as { loggedIn?: boolean }).loggedIn === false
    ) {
      tokenManager.clearAuth();
      window.location.href = "/login";
    }
    throw new ApiRequestError(message, code, res.status);
  }
  return data as T;
}

export async function apiGetRaw(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const url = buildUrl(path);
  const token = tokenManager.getToken();
  return fetch(url, {
    ...init,
    method: "GET",
    headers: {
      Accept: "*/*",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...userTimezoneHeaders(),
      ...(init?.headers && typeof init.headers === "object"
        ? init.headers
        : {}),
    },
  });
}

export async function apiSend<T>(
  path: string,
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  body?: unknown,
): Promise<T> {
  const url = buildUrl(path);
  const token = tokenManager.getToken();
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...userTimezoneHeaders(),
  };
  const hasBody = body !== undefined && method !== "GET";
  if (hasBody) headers["Content-Type"] = "application/json";
  const res = await fetch(url, {
    method,
    headers,
    body: hasBody ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const { message, code } = parseJsonError(data);
    if (
      data &&
      typeof data === "object" &&
      (data as { loggedIn?: boolean }).loggedIn === false
    ) {
      tokenManager.clearAuth();
      window.location.href = "/login";
    }
    throw new ApiRequestError(message, code, res.status);
  }
  if (method === "DELETE" && (data === null || data === "")) {
    return { deleted: true } as T;
  }
  return data as T;
}

export function getOmniWatchApiConfigured(): boolean {
  return getOmniWatchBaseUrl().length > 0;
}
